import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Key,
  Copy,
  Play,
  Table2,
  Code2,
  Terminal,
  Book,
  Eye,
  EyeOff,
  RefreshCw,
  Globe,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Zap,
  FileJson,
  Pencil,
  Check,
  X,
  AlertCircle,
  // Files, // Hidden for now
} from 'lucide-react';
import { mcpKeyService, MCPKey } from '../../services/mcpKeyService';
import { subdomainApiService, QueryResult, SubdomainConfig } from '../../services/subdomainApiService';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { TopNav } from '../ui/top-nav';
// import { MultiFileQueryPanel } from './MultiFileQueryPanel'; // Hidden for now

// Example queries for the playground
const EXAMPLE_QUERIES = [
  {
    name: 'Get first 10 rows',
    code: 'result = df.lazy().head(10).collect()',
  },
  {
    name: 'Filter and select columns',
    code: `result = df.lazy().filter(pl.col('column_name') > 100).select(['col1', 'col2']).collect()`,
  },
  {
    name: 'Group and aggregate',
    code: `result = df.lazy().group_by('category').agg([pl.count(), pl.col('value').sum()]).collect()`,
  },
  {
    name: 'Sort descending',
    code: `result = df.lazy().sort('column_name', descending=True).head(20).collect()`,
  },
];

export const DeveloperApiPage: React.FC = () => {
  // API Key state
  const [mcpKey, setMcpKey] = useState<MCPKey | null>(null);
  const [isLoadingKey, setIsLoadingKey] = useState(true);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Files state - use FileInfo from lambdaService (same as dashboard)
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');

  // Query state
  const [queryCode, setQueryCode] = useState(EXAMPLE_QUERIES[0].code);
  const [queryLimit, setQueryLimit] = useState(1000);
  const [isExecuting, setIsExecuting] = useState(false);
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [resultView, setResultView] = useState<'table' | 'json'>('table');

  // Query history (localStorage)
  const [queryHistory, setQueryHistory] = useState<{ file: string; code: string; timestamp: number }[]>([]);

  // Subdomain config for branded API URLs
  const [subdomainConfig, setSubdomainConfig] = useState<SubdomainConfig | null>(null);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [subdomainAvailability, setSubdomainAvailability] = useState<{ available: boolean; message?: string } | null>(null);
  const [isSavingSubdomain, setIsSavingSubdomain] = useState(false);

  // Load API key and subdomain config on mount
  useEffect(() => {
    loadApiKey();
    loadQueryHistory();
    loadSubdomainConfig();
  }, []);

  // Load files on mount (using JWT auth via lambdaService, same as dashboard)
  useEffect(() => {
    loadFiles();
  }, []);

  // Schema loading disabled - the DevAPI schema endpoint has CORS issues
  // The query playground works fine without schema info
  // useEffect(() => {
  //   if (selectedFile && mcpKey?.api_key) {
  //     loadFileSchema();
  //   } else {
  //     setFileSchema(null);
  //   }
  // }, [selectedFile, mcpKey]);

  const loadApiKey = async () => {
    try {
      const key = await mcpKeyService.getCurrentKey();
      setMcpKey(key);
    } catch (error) {
      // Silent fail - user may not have a key yet
    } finally {
      setIsLoadingKey(false);
    }
  };

  const loadSubdomainConfig = async () => {
    try {
      const config = await subdomainApiService.getSubdomainConfig();
      setSubdomainConfig(config);
    } catch (error) {
      // Silent fail - subdomain is optional
    }
  };

  const handleCheckSubdomain = async (subdomain: string) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailability(null);
      return;
    }
    setIsCheckingSubdomain(true);
    try {
      const result = await subdomainApiService.checkSubdomainAvailability(subdomain);
      setSubdomainAvailability(result);
    } catch (error) {
      setSubdomainAvailability({ available: false, message: 'Error checking availability' });
    } finally {
      setIsCheckingSubdomain(false);
    }
  };

  const handleSaveSubdomain = async () => {
    if (!newSubdomain || !subdomainAvailability?.available) return;

    setIsSavingSubdomain(true);
    try {
      const config = await subdomainApiService.saveSubdomainConfig(newSubdomain, true);
      setSubdomainConfig(config);
      setIsEditingSubdomain(false);
      setNewSubdomain('');
      setSubdomainAvailability(null);
      toast.success('API endpoint updated successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update subdomain');
    } finally {
      setIsSavingSubdomain(false);
    }
  };

  const startEditingSubdomain = () => {
    setNewSubdomain(subdomainConfig?.subdomain || '');
    setSubdomainAvailability(null);
    setIsEditingSubdomain(true);
  };

  const cancelEditingSubdomain = () => {
    setIsEditingSubdomain(false);
    setNewSubdomain('');
    setSubdomainAvailability(null);
  };

  const handleGenerateKey = async () => {
    if (mcpKey && !window.confirm('This will revoke your current API key. Continue?')) {
      return;
    }

    setIsGeneratingKey(true);
    try {
      const newKey = await mcpKeyService.generateKey();
      setMcpKey(newKey);
      setShowApiKey(true);
      toast.success('API Key generated successfully!');
    } catch (error) {
      toast.error('Failed to generate API key');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    try {
      // Use lambdaService (JWT auth) instead of subdomainApiService (X-API-Key)
      // This is the same approach used by the dashboard
      const result = await lambdaService.listFiles();
      if (result.success && result.files) {
        setFiles(result.files);
        if (result.files.length > 0 && !selectedFile) {
          setSelectedFile(result.files[0].fileName);
        }
      } else {
        toast.error(result.error || 'Failed to load files');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadQueryHistory = () => {
    try {
      const history = localStorage.getItem('bigsplice_query_history');
      if (history) {
        setQueryHistory(JSON.parse(history));
      }
    } catch (error) {
      // Silent fail
    }
  };

  const saveQueryToHistory = (file: string, code: string) => {
    const newHistory = [
      { file, code, timestamp: Date.now() },
      ...queryHistory.filter((h) => h.code !== code).slice(0, 9),
    ];
    setQueryHistory(newHistory);
    localStorage.setItem('bigsplice_query_history', JSON.stringify(newHistory));
  };

  const executeQuery = async () => {
    if (!selectedFile || !queryCode.trim()) {
      toast.error('Please select a file and enter a query');
      return;
    }

    setIsExecuting(true);
    setQueryResult(null);

    try {
      // Find the selected file to get its s3Key
      const fileInfo = files.find(f => f.fileName === selectedFile);
      const fileKey = fileInfo?.s3Key || selectedFile;

      // Use lambdaService.runAnalysis (JWT auth) instead of subdomainApiService (X-API-Key)
      // This matches the approach used by the Analyze page
      const analysisResult = await lambdaService.runAnalysis(
        selectedFile,
        queryCode,
        5, // maxParallelism
        'all', // queryScope
        'file', // processingLevel
        queryLimit, // analysisLimit
        queryLimit // resultLimit
      );

      if (analysisResult) {
        const result: QueryResult = {
          success: true,
          data: analysisResult.result,
          row_count: analysisResult.rowCount,
          execution_time_ms: analysisResult.executionTime,
          truncated: analysisResult.resultTruncated || false,
        };
        setQueryResult(result);
        saveQueryToHistory(selectedFile, queryCode);
        toast.success(`Query completed in ${analysisResult.executionTime}ms`);
      } else {
        throw new Error('Query returned no results');
      }
    } catch (error: any) {
      setQueryResult({
        success: false,
        data: [],
        row_count: 0,
        execution_time_ms: 0,
        truncated: false,
        error: error.message || 'Query execution failed',
      });
      toast.error(error.message || 'Query failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const maskKey = (key: string) => {
    if (key.length < 20) return key;
    return `${key.substring(0, 15)}...${key.substring(key.length - 8)}`;
  };

  const userEndpoint = subdomainConfig?.subdomain
    ? `https://${subdomainConfig.subdomain}.bigsplicer.com`
    : '';

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoadingKey) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6 space-y-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
          {/* Header */}
          <div className="flex items-center gap-3">
            <Code2 className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-base font-semibold text-foreground">Developer API</h1>
              <p className="text-sm text-muted-foreground">
                Query your data with Polars code via REST API
              </p>
            </div>
          </div>

          {/* Credentials Row — Endpoint + API Key side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* API Endpoint */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    <CardTitle className="text-sm">API Endpoint</CardTitle>
                  </div>
                  {subdomainConfig?.subdomain && !isEditingSubdomain && (
                    <div className="flex items-center gap-1.5">
                      <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-xs text-green-600 dark:text-green-400">Active</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isEditingSubdomain ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">https://</span>
                      <div className="relative flex-1">
                        <Input
                          value={newSubdomain}
                          onChange={(e) => {
                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            setNewSubdomain(value);
                            handleCheckSubdomain(value);
                          }}
                          placeholder="your-subdomain"
                          className="font-mono text-sm pr-10 h-9"
                        />
                        {isCheckingSubdomain && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {!isCheckingSubdomain && subdomainAvailability && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {subdomainAvailability.available ? (
                              <Check className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">.bigsplicer.com</span>
                    </div>
                    {subdomainAvailability && !subdomainAvailability.available && (
                      <p className="text-xs text-destructive">{subdomainAvailability.message || 'Not available'}</p>
                    )}
                    {subdomainAvailability?.available && (
                      <p className="text-xs text-green-600 dark:text-green-400">Available!</p>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveSubdomain} disabled={!subdomainAvailability?.available || isSavingSubdomain}>
                        {isSavingSubdomain ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving...</> : <><Check className="h-3.5 w-3.5 mr-1.5" />Save</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelEditingSubdomain}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={subdomainConfig?.subdomain ? `https://${subdomainConfig.subdomain}.bigsplicer.com` : 'Loading...'}
                        readOnly
                        className="font-mono text-xs bg-muted/50 h-9"
                      />
                      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!subdomainConfig?.subdomain}
                        onClick={() => copyToClipboard(`https://${subdomainConfig!.subdomain}.bigsplicer.com`, 'API endpoint')}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={startEditingSubdomain}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        {subdomainConfig?.subdomain ? 'Change' : 'Configure'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use with <code className="bg-muted px-1 py-0.5 rounded text-[11px]">X-API-Key</code> header.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* API Key */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-primary" />
                    <CardTitle className="text-sm">API Key</CardTitle>
                  </div>
                  {mcpKey && (
                    <div className="flex items-center gap-1.5">
                      <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-xs text-green-600 dark:text-green-400">Active</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {mcpKey ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          value={showApiKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}
                          readOnly
                          className="font-mono text-xs bg-muted/50 pr-10 h-9"
                          type={showApiKey ? 'text' : 'password'}
                        />
                        <Button variant="ghost" size="icon"
                          className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowApiKey(!showApiKey)}>
                          {showApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0"
                        onClick={() => copyToClipboard(mcpKey.api_key, 'API key')}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-9 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={handleGenerateKey} disabled={isGeneratingKey}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isGeneratingKey ? 'animate-spin' : ''}`} />
                        Regenerate
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use in <code className="bg-muted px-1 py-0.5 rounded text-[11px]">X-API-Key</code> header for all requests.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Key className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-3">No API key generated yet</p>
                    <Button size="sm" onClick={handleGenerateKey} disabled={isGeneratingKey}>
                      {isGeneratingKey ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating...</> : <><Key className="h-3.5 w-3.5 mr-1.5" />Generate API Key</>}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Query Playground with Tabs */}
          {mcpKey && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Terminal className="h-4 w-4 text-violet-500" />
                    <CardTitle className="text-sm">Query Playground</CardTitle>
                    <CardDescription className="text-xs ml-1">Test Polars queries before integrating</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={loadFiles} disabled={isLoadingFiles}>
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoadingFiles ? 'animate-spin' : ''}`} />
                    Refresh Files
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                {/* File Selection */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs">File</Label>
                    <Select value={selectedFile} onValueChange={setSelectedFile}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select a file..." />
                      </SelectTrigger>
                      <SelectContent>
                        {files.map((file) => (
                          <SelectItem key={file.s3Key || file.fileName} value={file.s3Key || file.fileName}>
                            {file.fileName}{file.fileSize > 0 ? ` (${formatBytes(file.fileSize)})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Row Limit</Label>
                    <Input type="number" value={queryLimit} onChange={(e) => setQueryLimit(parseInt(e.target.value) || 1000)}
                      min={1} max={10000} className="h-9" />
                  </div>
                </div>

                {/* Query Editor */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Polars Query</Label>
                    <Select value="" onValueChange={(value) => {
                      const example = EXAMPLE_QUERIES.find((e) => e.name === value);
                      if (example) setQueryCode(example.code);
                    }}>
                      <SelectTrigger className="w-[180px] h-7 text-xs">
                        <SelectValue placeholder="Load example..." />
                      </SelectTrigger>
                      <SelectContent>
                        {EXAMPLE_QUERIES.map((example) => (
                          <SelectItem key={example.name} value={example.name}>{example.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={queryCode} onChange={(e) => setQueryCode(e.target.value)}
                    placeholder="result = df.lazy().head(10).collect()"
                    className="font-mono text-xs min-h-[100px] resize-y" />
                  <p className="text-[11px] text-muted-foreground">
                    Assign result to <code className="bg-muted px-1 py-0.5 rounded">result</code>. Dataframe: <code className="bg-muted px-1 py-0.5 rounded">df</code>. Polars: <code className="bg-muted px-1 py-0.5 rounded">pl</code>.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={executeQuery} disabled={isExecuting || !selectedFile}>
                    {isExecuting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Running...</> : <><Play className="h-3.5 w-3.5 mr-1.5" />Run Query</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      copyToClipboard(
                        subdomainApiService.generateCurlCommand(
                          mcpKey.api_key,
                          selectedFile,
                          queryCode,
                          queryLimit,
                          subdomainConfig?.subdomain
                        ),
                        'curl command'
                      )
                    }
                    disabled={!selectedFile}
                  >
                    <Terminal className="h-4 w-4 mr-2" />
                    Copy as curl
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      copyToClipboard(
                        subdomainApiService.generatePythonCode(
                          mcpKey.api_key,
                          selectedFile,
                          queryCode,
                          queryLimit,
                          subdomainConfig?.subdomain
                        ),
                        'Python code'
                      )
                    }
                    disabled={!selectedFile}
                  >
                    <Code2 className="h-4 w-4 mr-2" />
                    Copy as Python
                  </Button>
                </div>

                {/* Results Panel */}
                {queryResult && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b">
                      <div className="flex items-center space-x-4">
                        {queryResult.success ? (
                          <div className="flex items-center space-x-2 text-green-600">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm font-medium">Success</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2 text-red-600">
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Error</span>
                          </div>
                        )}
                        {queryResult.success && (
                          <>
                            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                              <Table2 className="h-3 w-3" />
                              <span>{queryResult.row_count} rows</span>
                            </div>
                            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>{queryResult.execution_time_ms}ms</span>
                            </div>
                            {queryResult.truncated && (
                              <span className="text-xs text-amber-600">Truncated</span>
                            )}
                          </>
                        )}
                      </div>
                      {queryResult.success && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant={resultView === 'table' ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setResultView('table')}
                          >
                            <Table2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant={resultView === 'json' ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setResultView('json')}
                          >
                            <FileJson className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="max-h-[400px] overflow-auto">
                      {queryResult.error ? (
                        <div className="p-4 text-sm text-red-600 bg-red-50 dark:bg-red-950/20">
                          <pre className="whitespace-pre-wrap font-mono">{queryResult.error}</pre>
                        </div>
                      ) : resultView === 'table' && queryResult.data.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              {Object.keys(queryResult.data[0]).map((key) => (
                                <th
                                  key={key}
                                  className="px-4 py-2 text-left font-medium text-muted-foreground border-b"
                                >
                                  {key}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {queryResult.data.map((row, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                                {Object.values(row).map((value, j) => (
                                  <td key={j} className="px-4 py-2 font-mono text-xs">
                                    {value === null
                                      ? <span className="text-muted-foreground">null</span>
                                      : String(value)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <pre className="p-4 text-xs font-mono overflow-auto">
                          {JSON.stringify(queryResult.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
                </div>
                {/* Multi-File Query tab hidden for now
                  <TabsContent value="multi">
                    <MultiFileQueryPanel
                      files={files}
                      mcpKey={mcpKey}
                      subdomainConfig={subdomainConfig}
                    />
                  </TabsContent>
                */}
              </CardContent>
            </Card>
          )}

          {/* Documentation */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Book className="h-4 w-4 text-blue-500" />
                <CardTitle className="text-sm">API Documentation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="quickstart" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="quickstart">Quick Start</TabsTrigger>
                  <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                  <TabsTrigger value="examples">Examples</TabsTrigger>
                </TabsList>

                <TabsContent value="quickstart" className="space-y-4">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <h3 className="text-lg font-semibold mb-3">Getting Started</h3>
                    <ol className="space-y-4">
                      <li className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                          1
                        </span>
                        <div>
                          <p className="font-medium">Generate an API Key</p>
                          <p className="text-sm text-muted-foreground">
                            Use the API Key section above to generate your key.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                          2
                        </span>
                        <div>
                          <p className="font-medium">Upload Your Data</p>
                          <p className="text-sm text-muted-foreground">
                            Upload CSV, Excel, or Parquet files via the Upload page. They'll be
                            available for querying immediately.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                          3
                        </span>
                        <div>
                          <p className="font-medium">Test in the Playground</p>
                          <p className="text-sm text-muted-foreground">
                            Use the Query Playground above to test your Polars queries before
                            integrating them into your application.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                          4
                        </span>
                        <div>
                          <p className="font-medium">Integrate</p>
                          <p className="text-sm text-muted-foreground">
                            Use the "Copy as curl" or "Copy as Python" buttons to get code snippets
                            for your application.
                          </p>
                        </div>
                      </li>
                    </ol>
                  </div>
                </TabsContent>

                <TabsContent value="endpoints" className="space-y-6">
                  <div className="space-y-6">
                    {/* POST /query */}
                    <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-mono font-bold rounded">
                          POST
                        </span>
                        <code className="text-sm font-mono">/query</code>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">Execute a Polars query on a file</p>
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Request Body:</p>
                        <pre className="p-3 bg-slate-950 text-slate-50 rounded text-xs overflow-x-auto">
{`{
  "file": "sales_data.parquet",
  "code": "result = df.lazy().head(10).collect()",
  "limit": 1000
}`}
                        </pre>
                        <p className="font-medium mt-3">Response:</p>
                        <pre className="p-3 bg-slate-950 text-slate-50 rounded text-xs overflow-x-auto">
{`{
  "success": true,
  "data": [...],
  "row_count": 10,
  "execution_time_ms": 45,
  "truncated": false
}`}
                        </pre>
                      </div>
                    </div>

                    {/* GET /files */}
                    <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-mono font-bold rounded">
                          GET
                        </span>
                        <code className="text-sm font-mono">/files</code>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">List all available files</p>
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Response:</p>
                        <pre className="p-3 bg-slate-950 text-slate-50 rounded text-xs overflow-x-auto">
{`{
  "files": [
    {
      "name": "sales_data.parquet",
      "size_bytes": 15234567,
      "is_chunked": false
    }
  ],
  "count": 1
}`}
                        </pre>
                      </div>
                    </div>

                    {/* GET /schema/{file} */}
                    <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-mono font-bold rounded">
                          GET
                        </span>
                        <code className="text-sm font-mono">/schema/{'{file}'}</code>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">Get schema for a specific file</p>
                      <div className="space-y-2 text-sm">
                        <p className="font-medium">Response:</p>
                        <pre className="p-3 bg-slate-950 text-slate-50 rounded text-xs overflow-x-auto">
{`{
  "schema": {
    "customer": "Utf8",
    "revenue": "Float64",
    "date": "Date"
  }
}`}
                        </pre>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="examples" className="space-y-6">
                  <div className="space-y-6">
                    {/* Python Example */}
                    <div className="space-y-2">
                      <h4 className="font-medium flex items-center space-x-2">
                        <Code2 className="h-4 w-4" />
                        <span>Python</span>
                      </h4>
                      <pre className="p-4 bg-slate-950 text-slate-50 rounded-lg text-xs overflow-x-auto">
{`import requests

API_KEY = "your_api_key"
ENDPOINT = "${userEndpoint}"

# List files
files = requests.get(
    f"{ENDPOINT}/files",
    headers={"X-API-Key": API_KEY}
).json()

print(f"Available files: {[f['name'] for f in files['files']]}")

# Execute query
result = requests.post(
    f"{ENDPOINT}/query",
    headers={
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
    },
    json={
        "file": "sales_data.parquet",
        "code": "result = df.lazy().group_by('category').agg(pl.sum('revenue')).collect()",
        "limit": 1000
    }
).json()

if result["success"]:
    print(f"Got {result['row_count']} rows in {result['execution_time_ms']}ms")
    for row in result["data"]:
        print(row)`}
                      </pre>
                    </div>

                    {/* JavaScript Example */}
                    <div className="space-y-2">
                      <h4 className="font-medium flex items-center space-x-2">
                        <FileJson className="h-4 w-4" />
                        <span>JavaScript / Node.js</span>
                      </h4>
                      <pre className="p-4 bg-slate-950 text-slate-50 rounded-lg text-xs overflow-x-auto">
{`const API_KEY = "your_api_key";
const ENDPOINT = "${userEndpoint}";

// List files
const filesRes = await fetch(\`\${ENDPOINT}/files\`, {
  headers: { "X-API-Key": API_KEY }
});
const { files } = await filesRes.json();
console.log("Available files:", files.map(f => f.name));

// Execute query
const result = await fetch(\`\${ENDPOINT}/query\`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY
  },
  body: JSON.stringify({
    file: "sales_data.parquet",
    code: "result = df.lazy().head(100).collect()",
    limit: 1000
  })
}).then(r => r.json());

if (result.success) {
  console.log(\`Got \${result.row_count} rows in \${result.execution_time_ms}ms\`);
  console.table(result.data.slice(0, 5));
}`}
                      </pre>
                    </div>

                    {/* curl Example */}
                    <div className="space-y-2">
                      <h4 className="font-medium flex items-center space-x-2">
                        <Terminal className="h-4 w-4" />
                        <span>curl</span>
                      </h4>
                      <pre className="p-4 bg-slate-950 text-slate-50 rounded-lg text-xs overflow-x-auto">
{`# List files
curl -H "X-API-Key: your_api_key" \\
  "${userEndpoint}/files"

# Execute query
curl -X POST "${userEndpoint}/query" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key" \\
  -d '{"file":"sales_data.parquet","code":"result = df.lazy().head(10).collect()","limit":1000}'`}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Features/Capabilities */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800/50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <Zap className="h-5 w-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold">Fast Queries</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Leverage Polars' blazing-fast query engine. Most queries complete in under 500ms.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800/50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold">REST API</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Simple REST interface works with any language. No special SDK required.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20 border-purple-200 dark:border-purple-800/50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Key className="h-5 w-5 text-purple-600" />
                  </div>
                  <h4 className="font-semibold">Secure</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  API key authentication ensures only you can access your data. Rate limited for
                  protection.
                </p>
              </CardContent>
            </Card>
          </div>
      </main>
    </div>
  );
};
